// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Project Detail Page
// Route: /projects/$projectId
// apps/web/src/routes/projects.$projectId.tsx
//
// Deep-dive into a single project: phases with progress bars, work items
// with complexity breakdowns, compression ratio hero, and phase effort
// comparison. Every metric explorable via EngineTooltip.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  NeonValue,
  StatusBadge,
  EngineTooltip,
  EmptyState,
  LoadingShimmer,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import { useProject, usePhases, useWorkItems, useWorkItemMutations } from '../lib/api/projects';
import type { Phase, WorkItem, WorkItemStatus, PhaseStatus } from '../lib/api/types';
import { ComplexityDefaultsPanel } from '../features/project/ComplexityDefaultsPanel';

// ─── Route Definition ───────────────────────────────────────────────────────

export const Route = createFileRoute('/projects/$projectId')({
  component: ProjectDetailPage,
});

// ─── Status Colors ──────────────────────────────────────────────────────────

const PHASE_STATUS_COLOR: Record<PhaseStatus, string> = {
  NotStarted: C.dim,
  InProgress: C.cyan,
  Completed: C.neon,
  Blocked: C.coral,
};

const WORK_ITEM_STATUS: Record<WorkItemStatus, { color: string; icon: string }> = {
  Todo: { color: C.dim, icon: '○' },
  InProgress: { color: C.cyan, icon: '◉' },
  InReview: { color: C.violet, icon: '◎' },
  Completed: { color: C.neon, icon: '✓' },
  Blocked: { color: C.coral, icon: '✕' },
};

// Complexity dimension labels
const COMPLEXITY_LABELS = [
  { key: 'architecturalDepth' as const, label: 'ARCH DEPTH', color: C.violet },
  { key: 'integrationPoints' as const, label: 'INTEGRATION', color: C.cyan },
  { key: 'domainExpertise' as const, label: 'DOMAIN', color: C.amber },
  { key: 'infrastructureExpertise' as const, label: 'INFRA', color: C.blue },
  { key: 'securityExpertise' as const, label: 'SECURITY', color: C.coral },
  { key: 'novelty' as const, label: 'NOVELTY', color: C.pink },
];

// ─── Main Component ─────────────────────────────────────────────────────────

function ProjectDetailPage() {
  const { projectId } = Route.useParams();
  const id = parseInt(projectId, 10);

  const { data: project, loading: projLoading, error: projError } = useProject(id);
  const { data: phases, loading: phasesLoading } = usePhases(id);
  const { data: workItems, loading: wiLoading, refetch: refetchWorkItems } = useWorkItems(id);

  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);
  const [showAddWorkItem, setShowAddWorkItem] = useState<number | null>(null);

  // ── Derived metrics ────────────────────────────────────────────────────

  const phaseMetrics = useMemo(() => {
    if (!phases) return null;
    const completed = phases.filter((p) => p.status === 'Completed').length;
    const totalPlanned = phases.reduce((s, p) => s + p.plannedEffortWeeks, 0);
    const totalEquiv = phases.reduce((s, p) => s + p.equivalentEffortWeeks, 0);
    return { total: phases.length, completed, totalPlanned, totalEquiv };
  }, [phases]);

  const workItemMetrics = useMemo(() => {
    if (!workItems) return null;
    const completed = workItems.filter((w) => w.status === 'Completed').length;
    const avgComplexity =
      workItems.length > 0
        ? workItems.reduce((s, w) => s + (w.complexityScore ?? 0), 0) / workItems.length
        : 0;
    return { total: workItems.length, completed, avgComplexity };
  }, [workItems]);

  // ── Animated counters ──────────────────────────────────────────────────

  const countCompression = useCounter(
    project ? Math.round(project.compressionRatio * 10) / 10 : 0,
    1000,
    300
  );
  const countEquivWeeks = useCounter(
    project ? Math.round(project.totalEquivalentWeeks) : 0,
    800,
    400
  );
  const countActualHours = useCounter(
    project ? Math.round(project.totalActualHours) : 0,
    800,
    500
  );
  const countReplCost = useCounter(
    project ? Math.round(project.replacementCostEstimate / 1000) : 0,
    900,
    600
  );

  // ── Loading ────────────────────────────────────────────────────────────

  if (projLoading) {
    return (
      <EnginePageShell title="Loading..." subtitle="PROJECT DETAIL" icon="🏗" accentColor={C.cyan}>
        <LoadingShimmer height={200} />
      </EnginePageShell>
    );
  }

  if (projError || !project) {
    return (
      <EnginePageShell title="Error" subtitle="PROJECT NOT FOUND" icon="⚠" accentColor={C.coral}>
        <GlassPanel accent={C.coral}>
          <EmptyState
            icon="⚠"
            title="Project not found"
            subtitle={projError?.message || 'This project may have been deleted or you don\'t have access.'}
            action={
              <Link to="/projects" style={{ color: C.cyan, fontFamily: F.mono, fontSize: 12 }}>
                ← Back to Projects
              </Link>
            }
          />
        </GlassPanel>
      </EnginePageShell>
    );
  }

  return (
    <EnginePageShell
      title={project.name}
      subtitle="PROJECT DETAIL"
      icon="🏗"
      accentColor={C.cyan}
      statusLabel={project.status.toUpperCase()}
      statusColor={
        project.status === 'Active'
          ? C.neon
          : project.status === 'Completed'
          ? C.cyan
          : C.amber
      }
      actions={
        <Link
          to="/projects"
          style={{
            fontSize: 11,
            fontFamily: F.mono,
            color: C.muted,
            textDecoration: 'none',
            letterSpacing: '0.05em',
          }}
        >
          ← PROJECTS
        </Link>
      }
    >
      {/* ══════════ HERO — Compression Ratio & Key Metrics ══════════ */}
      <GlassPanel accent={C.amber} delay={0.1} style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 40, flexWrap: 'wrap' }}>
          {/* Compression ratio — the star */}
          <EngineTooltip
            content={
              <div style={{ maxWidth: 280 }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: C.text }}>Compression Ratio</div>
                <div style={{ fontSize: 12, color: C.textSoft, lineHeight: 1.5 }}>
                  This project delivers engineering output{' '}
                  <span style={{ color: C.amber, fontWeight: 600 }}>
                    {(project.compressionRatio ?? 0).toFixed(1)}× faster
                  </span>{' '}
                  than a traditional team. Equivalent effort of{' '}
                  <span style={{ color: C.violet, fontFamily: F.mono }}>
                    {(project.totalEquivalentWeeks ?? 0).toFixed(1)} weeks
                  </span>{' '}
                  delivered in{' '}
                  <span style={{ color: C.textSoft, fontFamily: F.mono }}>
                    {(project.totalActualHours ?? 0).toFixed(0)} actual hours
                  </span>.
                </div>
              </div>
            }
          >
            <div style={{ textAlign: 'center', minWidth: 140 }}>
              <div
                style={{
                  fontSize: 9,
                  fontFamily: F.mono,
                  letterSpacing: '0.15em',
                  color: C.muted,
                  marginBottom: 6,
                }}
              >
                COMPRESSION RATIO
              </div>
              <NeonValue color={C.amber} size={52} glow>
                {countCompression}×
              </NeonValue>
            </div>
          </EngineTooltip>

          {/* Separator */}
          <div style={{ width: 1, height: 60, background: C.faint }} />

          {/* Secondary metrics grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, flex: 1 }}>
            <EngineTooltip
              content={<div>Total human hours logged across all sessions.</div>}
            >
              <div>
                <div style={{ fontSize: 9, fontFamily: F.mono, letterSpacing: '0.1em', color: C.dim, marginBottom: 4 }}>
                  ACTUAL HOURS
                </div>
                <NeonValue color={C.textSoft} size={28}>
                  {countActualHours}
                </NeonValue>
                <span style={{ fontSize: 11, fontFamily: F.mono, color: C.dim, marginLeft: 4 }}>hrs</span>
              </div>
            </EngineTooltip>

            <EngineTooltip
              content={<div>What a traditional team would need to deliver this scope.</div>}
            >
              <div>
                <div style={{ fontSize: 9, fontFamily: F.mono, letterSpacing: '0.1em', color: C.dim, marginBottom: 4 }}>
                  EQUIVALENT WEEKS
                </div>
                <NeonValue color={C.violet} size={28}>
                  {countEquivWeeks}
                </NeonValue>
                <span style={{ fontSize: 11, fontFamily: F.mono, color: C.dim, marginLeft: 4 }}>wks</span>
              </div>
            </EngineTooltip>

            <EngineTooltip
              content={<div>Market-rate cost to build with a traditional team at $150–$250/hr.</div>}
            >
              <div>
                <div style={{ fontSize: 9, fontFamily: F.mono, letterSpacing: '0.1em', color: C.dim, marginBottom: 4 }}>
                  REPLACEMENT COST
                </div>
                <NeonValue color={C.neon} size={28}>
                  ${countReplCost}K
                </NeonValue>
              </div>
            </EngineTooltip>
          </div>
        </div>

        {/* Project description */}
        {project.description && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 14,
              borderTop: `1px solid ${C.faint}`,
              fontSize: 13,
              color: C.muted,
              lineHeight: 1.6,
            }}
          >
            {project.description}
          </div>
        )}

        {/* Tags + repo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          {project.repositoryUrl && (
            <a
              href={project.repositoryUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 10,
                fontFamily: F.mono,
                color: C.cyan,
                textDecoration: 'none',
                background: `${C.cyan}08`,
                border: `1px solid ${C.cyan}20`,
                padding: '3px 10px',
                borderRadius: 4,
              }}
            >
              ⟁ {project.repositoryUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\.git$/, '')}
            </a>
          )}
          {project.tags?.map((tag) => (
            <span
              key={tag}
              style={{
                fontSize: 9,
                fontFamily: F.mono,
                color: C.dim,
                background: `${C.violet}08`,
                border: `1px solid ${C.violet}15`,
                padding: '2px 8px',
                borderRadius: 4,
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </GlassPanel>

      {/* ══════════ COMPLEXITY DEFAULTS ══════════ */}
      <ComplexityDefaultsPanel projectId={id} />

      {/* ══════════ PHASES ══════════ */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <SectionLabel color={C.cyan}>
          Phases {phaseMetrics ? `(${phaseMetrics.completed}/${phaseMetrics.total} complete)` : ''}
        </SectionLabel>
      </div>

      {phasesLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[0, 1, 2].map((i) => (
            <GlassPanel key={i} delay={0.2 + i * 0.08}>
              <LoadingShimmer height={60} />
            </GlassPanel>
          ))}
        </div>
      ) : phases && phases.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
          {phases
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map((phase, i) => (
              <PhaseRow
                key={phase.id}
                phase={phase}
                index={i}
                expanded={expandedPhase === phase.id}
                onToggle={() => setExpandedPhase(expandedPhase === phase.id ? null : phase.id)}
                workItems={(workItems || []).filter((wi) => wi.phaseId === phase.id)}
                wiLoading={wiLoading}
                showAddWorkItem={showAddWorkItem === phase.id}
                onToggleAddWorkItem={() =>
                  setShowAddWorkItem(showAddWorkItem === phase.id ? null : phase.id)
                }
                onWorkItemCreated={() => {
                  refetchWorkItems();
                  setShowAddWorkItem(null);
                }}
              />
            ))}
        </div>
      ) : (
        <GlassPanel accent={C.cyan} delay={0.3} style={{ marginBottom: 28 }}>
          <EmptyState
            icon="📐"
            title="No phases yet"
            subtitle="Phases organize your project into major milestones. Add your first phase to start structuring the work."
          />
        </GlassPanel>
      )}

      {/* ══════════ ALL WORK ITEMS TABLE ══════════ */}
      <SectionLabel color={C.violet}>
        All Work Items {workItemMetrics ? `(${workItemMetrics.completed}/${workItemMetrics.total})` : ''}
      </SectionLabel>

      <GlassPanel accent={C.violet} delay={0.5} style={{ padding: 0, overflow: 'hidden' }}>
        {wiLoading ? (
          <div style={{ padding: 20 }}>
            <LoadingShimmer height={200} />
          </div>
        ) : workItems && workItems.length > 0 ? (
          <WorkItemsTable workItems={workItems} />
        ) : (
          <EmptyState
            icon="📋"
            title="No work items"
            subtitle="Work items are discrete engineering tasks within phases. They capture complexity scores and equivalent effort."
          />
        )}
      </GlassPanel>
    </EnginePageShell>
  );
}

// ─── PhaseRow ───────────────────────────────────────────────────────────────

function PhaseRow({
  phase,
  index,
  expanded,
  onToggle,
  workItems,
  wiLoading: _wiLoading,
  showAddWorkItem,
  onToggleAddWorkItem,
  onWorkItemCreated,
}: {
  phase: Phase;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  workItems: WorkItem[];
  wiLoading: boolean;
  showAddWorkItem: boolean;
  onToggleAddWorkItem: () => void;
  onWorkItemCreated: () => void;
}) {
  const statusColor = PHASE_STATUS_COLOR[phase.status] || C.dim;
  const completedItems = workItems.filter((w) => w.status === 'Completed').length;
  const progress = workItems.length > 0 ? (completedItems / workItems.length) * 100 : 0;
  const delay = 0.25 + index * 0.06;

  return (
    <div
      style={{
        opacity: 0,
        transform: 'translateY(10px)',
        animation: `engine-feed-in 0.4s cubic-bezier(0.16,1,0.3,1) ${delay}s forwards`,
      }}
    >
      {/* Phase header — clickable */}
      <div
        onClick={onToggle}
        style={{
          background: expanded ? C.cardHover : C.surface,
          border: `1px solid ${expanded ? `${statusColor}30` : C.faint}`,
          borderRadius: expanded ? '12px 12px 0 0' : 12,
          padding: '14px 18px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          transition: 'all 0.2s',
        }}
      >
        {/* Expand indicator */}
        <span
          style={{
            fontSize: 10,
            color: C.dim,
            transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
            transition: 'transform 0.2s',
            fontFamily: F.mono,
          }}
        >
          ▶
        </span>

        {/* Order badge */}
        <span
          style={{
            fontSize: 10,
            fontFamily: F.mono,
            fontWeight: 700,
            color: statusColor,
            background: `${statusColor}15`,
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
            flexShrink: 0,
          }}
        >
          {phase.orderIndex + 1}
        </span>

        {/* Phase name + status */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: C.text,
                fontFamily: F.display,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {phase.name}
            </span>
            <StatusBadge label={phase.status} color={statusColor} />
          </div>

          {/* Progress bar */}
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                flex: 1,
                height: 3,
                background: C.faint,
                borderRadius: 2,
                overflow: 'hidden',
                maxWidth: 200,
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: `linear-gradient(90deg, ${statusColor}, ${statusColor}cc)`,
                  borderRadius: 2,
                  animation: 'engine-bar-fill 0.8s cubic-bezier(0.16,1,0.3,1)',
                  boxShadow: progress > 0 ? `0 0 6px ${statusColor}40` : undefined,
                }}
              />
            </div>
            <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>
              {completedItems}/{workItems.length}
            </span>
          </div>
        </div>

        {/* Phase effort metrics */}
        <div style={{ display: 'flex', gap: 20, flexShrink: 0 }}>
          <EngineTooltip content={<div>Planned effort estimate for this phase.</div>}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>PLANNED</div>
              <div style={{ fontSize: 14, fontFamily: F.mono, fontWeight: 600, color: C.textSoft }}>
                {phase.plannedEffortWeeks}<span style={{ fontSize: 9, color: C.dim }}> wks</span>
              </div>
            </div>
          </EngineTooltip>

          <EngineTooltip content={<div>Equivalent effort a traditional team would need.</div>}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>EQUIV</div>
              <div style={{ fontSize: 14, fontFamily: F.mono, fontWeight: 600, color: C.violet }}>
                {phase.equivalentEffortWeeks}<span style={{ fontSize: 9, color: C.dim }}> wks</span>
              </div>
            </div>
          </EngineTooltip>

          <EngineTooltip content={<div>Actual calendar days this phase has taken so far.</div>}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>ACTUAL</div>
              <div style={{ fontSize: 14, fontFamily: F.mono, fontWeight: 600, color: C.cyan }}>
                {phase.actualCalendarDays}<span style={{ fontSize: 9, color: C.dim }}> days</span>
              </div>
            </div>
          </EngineTooltip>
        </div>
      </div>

      {/* Expanded — work items within this phase */}
      {expanded && (
        <div
          style={{
            background: 'rgba(6,12,24,0.6)',
            border: `1px solid ${statusColor}20`,
            borderTop: 'none',
            borderRadius: '0 0 12px 12px',
            padding: 16,
          }}
        >
          {/* Expertise domains */}
          {phase.expertiseDomains && phase.expertiseDomains.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              {phase.expertiseDomains.map((domain) => (
                <span
                  key={domain}
                  style={{
                    fontSize: 9,
                    fontFamily: F.mono,
                    color: C.violet,
                    background: `${C.violet}10`,
                    border: `1px solid ${C.violet}20`,
                    padding: '2px 8px',
                    borderRadius: 4,
                    letterSpacing: '0.05em',
                  }}
                >
                  {domain}
                </span>
              ))}
            </div>
          )}

          {/* Work items in this phase */}
          {workItems.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {workItems
                .sort((a, b) => (a.status === 'Completed' ? 1 : 0) - (b.status === 'Completed' ? 1 : 0))
                .map((wi, i) => (
                  <WorkItemRow key={wi.id} item={wi} index={i} />
                ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: C.dim, textAlign: 'center', padding: 20 }}>
              No work items in this phase yet.
            </div>
          )}

          {/* Add work item button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleAddWorkItem();
            }}
            style={{
              marginTop: 10,
              width: '100%',
              background: 'transparent',
              border: `1px dashed ${C.faint}`,
              borderRadius: 8,
              padding: '8px 0',
              color: C.dim,
              fontFamily: F.mono,
              fontSize: 10,
              cursor: 'pointer',
              letterSpacing: '0.08em',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = `${C.neon}40`;
              e.currentTarget.style.color = C.neon;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = C.faint;
              e.currentTarget.style.color = C.dim;
            }}
          >
            + ADD WORK ITEM
          </button>

          {/* Inline add work item form */}
          {showAddWorkItem && (
            <AddWorkItemInline
              phaseId={phase.id}
              onCreated={onWorkItemCreated}
              onCancel={onToggleAddWorkItem}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── WorkItemRow ────────────────────────────────────────────────────────────

function WorkItemRow({ item, index }: { item: WorkItem; index: number }) {
  const [hovered, setHovered] = useState(false);
  const statusCfg = WORK_ITEM_STATUS[item.status] || WORK_ITEM_STATUS.Todo;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        background: hovered ? 'rgba(16,28,52,0.5)' : 'transparent',
        borderRadius: 8,
        transition: 'background 0.15s',
        opacity: 0,
        animation: `engine-feed-in 0.3s ease ${index * 0.04}s forwards`,
      }}
    >
      {/* Status icon */}
      <span style={{ fontFamily: F.mono, fontSize: 12, color: statusCfg.color, flexShrink: 0 }}>
        {statusCfg.icon}
      </span>

      {/* Name + description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: item.status === 'Completed' ? C.muted : C.text,
            textDecoration: item.status === 'Completed' ? 'line-through' : undefined,
            opacity: item.status === 'Completed' ? 0.7 : 1,
          }}
        >
          {item.name}
        </div>
        {item.description && (
          <div
            style={{
              fontSize: 11,
              color: C.dim,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 400,
              marginTop: 2,
            }}
          >
            {item.description}
          </div>
        )}
      </div>

      {/* Complexity mini-bar */}
      <EngineTooltip
        content={
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8, color: C.text }}>
              Complexity Score: {(item.complexityScore ?? 0).toFixed(1)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {COMPLEXITY_LABELS.map(({ key, label, color }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, width: 80 }}>{label}</span>
                  <div style={{ width: 60, height: 4, background: C.faint, borderRadius: 2 }}>
                    <div
                      style={{
                        width: `${(((item as any)[key] || 0) / 5) * 100}%`,
                        height: '100%',
                        background: color,
                        borderRadius: 2,
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 10, fontFamily: F.mono, color }}>{(item as any)[key] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <ComplexityMiniBar score={item.complexityScore ?? 0} maxScore={30} />
          <span style={{ fontSize: 11, fontFamily: F.mono, color: C.muted, minWidth: 24, textAlign: 'right' }}>
            {(item.complexityScore ?? 0).toFixed(0)}
          </span>
        </div>
      </EngineTooltip>

      {/* Equivalent weeks */}
      <EngineTooltip content={<div>Equivalent engineering weeks for this work item.</div>}>
        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 50 }}>
          <span style={{ fontSize: 13, fontFamily: F.mono, fontWeight: 600, color: C.violet }}>
            {(item.equivalentWeeks ?? 0).toFixed(1)}
          </span>
          <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim }}> wks</span>
        </div>
      </EngineTooltip>

      {/* Actual hours */}
      <EngineTooltip content={<div>Actual hours logged.</div>}>
        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 44 }}>
          <span style={{ fontSize: 13, fontFamily: F.mono, color: C.textSoft }}>
            {(item.actualHours ?? 0).toFixed(0)}
          </span>
          <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim }}> hrs</span>
        </div>
      </EngineTooltip>
    </div>
  );
}

// ─── ComplexityMiniBar ──────────────────────────────────────────────────────

function ComplexityMiniBar({ score, maxScore }: { score: number; maxScore: number }) {
  const pct = Math.min((score / maxScore) * 100, 100);
  const color =
    pct > 70 ? C.coral : pct > 40 ? C.amber : C.neon;

  return (
    <div style={{ width: 40, height: 4, background: C.faint, borderRadius: 2 }}>
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          borderRadius: 2,
          animation: 'engine-bar-fill 0.6s cubic-bezier(0.16,1,0.3,1)',
        }}
      />
    </div>
  );
}

// ─── WorkItemsTable (full table view) ───────────────────────────────────────

function WorkItemsTable({ workItems }: { workItems: WorkItem[] }) {
  const [sortBy, setSortBy] = useState<'complexityScore' | 'equivalentWeeks' | 'actualHours' | 'name'>('complexityScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    return [...workItems].sort((a, b) => {
      const av = sortBy === 'name' ? a.name : (a as any)[sortBy];
      const bv = sortBy === 'name' ? b.name : (b as any)[sortBy];
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [workItems, sortBy, sortDir]);

  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const headerStyle = (col: typeof sortBy): React.CSSProperties => ({
    fontSize: 9,
    fontFamily: F.mono,
    letterSpacing: '0.1em',
    color: sortBy === col ? C.cyan : C.dim,
    cursor: 'pointer',
    userSelect: 'none' as const,
    textAlign: 'right' as const,
    padding: '10px 14px',
    borderBottom: `1px solid ${C.faint}`,
    transition: 'color 0.15s',
  });

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...headerStyle('name'), textAlign: 'left' }} onClick={() => toggleSort('name')}>
              WORK ITEM {sortBy === 'name' && (sortDir === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ width: 80, ...headerStyle('name'), textAlign: 'center', cursor: 'default', color: C.dim }}>
              STATUS
            </th>
            <th style={{ width: 100, ...headerStyle('complexityScore') }} onClick={() => toggleSort('complexityScore')}>
              COMPLEXITY {sortBy === 'complexityScore' && (sortDir === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ width: 100, ...headerStyle('equivalentWeeks') }} onClick={() => toggleSort('equivalentWeeks')}>
              EQUIV {sortBy === 'equivalentWeeks' && (sortDir === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ width: 80, ...headerStyle('actualHours') }} onClick={() => toggleSort('actualHours')}>
              ACTUAL {sortBy === 'actualHours' && (sortDir === 'asc' ? '↑' : '↓')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((wi, i) => {
            const statusCfg = WORK_ITEM_STATUS[wi.status] || WORK_ITEM_STATUS.Todo;
            return (
              <tr
                key={wi.id}
                style={{
                  borderBottom: `1px solid ${C.faint}`,
                  opacity: 0,
                  animation: `engine-feed-in 0.3s ease ${i * 0.03}s forwards`,
                }}
              >
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{wi.name}</div>
                  {wi.phaseName && (
                    <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>{wi.phaseName}</div>
                  )}
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                  <StatusBadge label={wi.status} color={statusCfg.color} />
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                  <EngineTooltip
                    content={
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 6, color: C.text }}>
                          Complexity: {(wi.complexityScore ?? 0).toFixed(1)}
                        </div>
                        {COMPLEXITY_LABELS.map(({ key, label, color }) => (
                          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 11 }}>
                            <span style={{ color: C.dim }}>{label}</span>
                            <span style={{ fontFamily: F.mono, color }}>{(wi as any)[key] || 0}/5</span>
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <span style={{ fontFamily: F.mono, fontSize: 13, color: C.amber, fontWeight: 600 }}>
                      {(wi.complexityScore ?? 0).toFixed(1)}
                    </span>
                  </EngineTooltip>
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: F.mono, fontSize: 13, color: C.violet, fontWeight: 600 }}>
                  {(wi.equivalentWeeks ?? 0).toFixed(1)}<span style={{ fontSize: 9, color: C.dim }}> wks</span>
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: F.mono, fontSize: 13, color: C.textSoft }}>
                  {(wi.actualHours ?? 0).toFixed(0)}<span style={{ fontSize: 9, color: C.dim }}> hrs</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── AddWorkItemInline ──────────────────────────────────────────────────────

function AddWorkItemInline({
  phaseId,
  onCreated,
  onCancel,
}: {
  phaseId: number;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const { create, loading } = useWorkItemMutations();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) return;
    const result = await create({ phaseId, name: name.trim(), description: description.trim() || undefined });
    if (result) onCreated();
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(0,0,0,0.3)',
    border: `1px solid ${C.faint}`,
    borderRadius: 6,
    padding: '8px 12px',
    color: C.text,
    fontFamily: F.display,
    fontSize: 12,
    outline: 'none',
  };

  return (
    <div
      style={{
        marginTop: 10,
        padding: 14,
        background: `${C.neon}05`,
        border: `1px solid ${C.neon}15`,
        borderRadius: 8,
        animation: 'engine-tooltip-in 0.2s ease',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input
          type="text"
          placeholder="Work item name..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />
        <input
          type="text"
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={inputStyle}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              background: 'transparent',
              border: `1px solid ${C.faint}`,
              color: C.dim,
              fontFamily: F.mono,
              fontSize: 10,
              padding: '5px 14px',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            CANCEL
          </button>
          <button
            disabled={!name.trim() || loading}
            onClick={handleSubmit}
            style={{
              background: name.trim() ? `${C.neon}15` : 'transparent',
              border: `1px solid ${name.trim() ? `${C.neon}30` : C.faint}`,
              color: name.trim() ? C.neon : C.dim,
              fontFamily: F.mono,
              fontSize: 10,
              fontWeight: 600,
              padding: '5px 16px',
              borderRadius: 4,
              cursor: name.trim() ? 'pointer' : 'not-allowed',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'ADDING...' : 'ADD'}
          </button>
        </div>
      </div>
    </div>
  );
}
